import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapaEventos.css';

// Importar el nuevo sistema de clustering profesional
import { MarkerClusterComponent } from './MarkerCluster';

// Fix para iconos de Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

// Configurar iconos por defecto de Leaflet de forma más robusta
let DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Componente para control inicial del mapa
function MapController({ eventos, selectedEvent }) {
  const map = useMap();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Solo ejecutar una vez al cargar
    if (hasInitialized.current || !eventos || eventos.length === 0) return;
    
    const validCoords = eventos.filter(evento => {
      const lat = parseFloat(evento.latitud);
      const lng = parseFloat(evento.longitud);
      return lat && lng && !isNaN(lat) && !isNaN(lng);
    });

    if (validCoords.length > 0) {
      if (selectedEvent) {
        const selectedLat = parseFloat(selectedEvent.latitud);
        const selectedLng = parseFloat(selectedEvent.longitud);
        map.setView([selectedLat, selectedLng], 16);
      } else if (validCoords.length === 1) {
        const lat = parseFloat(validCoords[0].latitud);
        const lng = parseFloat(validCoords[0].longitud);
        map.setView([lat, lng], 14);
      } else {
        const bounds = L.latLngBounds(
          validCoords.map(evento => {
            const lat = parseFloat(evento.latitud);
            const lng = parseFloat(evento.longitud);
            return [lat, lng];
          })
        );
        map.fitBounds(bounds, { padding: [20, 20] });
      }
      
      hasInitialized.current = true;
    }
  }, [map, eventos, selectedEvent]);

  return null;
}

// Componente simplificado que usa clustering profesional
function DynamicMarkers({ eventos, selectedEvent, vehiculo, showPolyline = false, showNumbers = false }) {
  // Usar el componente de clustering profesional directamente
  return (
    <MarkerClusterComponent
      eventos={eventos}
      selectedEvent={selectedEvent}
      vehiculo={vehiculo}
      showPolyline={showPolyline}
      showNumbers={showNumbers}
    />
  );
}

export default function MapaEventos({ 
  eventos = [], 
  selectedEvent = null, 
  vehiculo = null,
  altura = '500px',
  showPolyline = false,
  showNumbers = false
}) {
  const mapRef = useRef();
  
  console.log("MapaEventos: Componente principal renderizado");

  // Filtrar eventos con coordenadas válidas para mensaje vacío
  const eventosValidos = eventos.filter(evento => {
    const lat = parseFloat(evento.latitud);
    const lng = parseFloat(evento.longitud);
    return lat && lng && 
          !isNaN(lat) && !isNaN(lng) &&
          lat >= -90 && lat <= 90 &&
          lng >= -180 && lng <= 180 &&
          lat !== 0 && lng !== 0;
  });

  // Coordenadas por defecto (México City)
  const defaultCenter = [19.4326, -99.1332];
  const defaultZoom = 6;

  if (eventosValidos.length === 0) {
    return (
      <div className="mapa-eventos-container d-flex align-items-center justify-content-center h-100 bg-light">
        <div className="text-center">
          <i className="fas fa-map-marked-alt fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Sin coordenadas GPS</h5>
          <p className="text-muted">
            {eventos.length > 0 
              ? 'Los eventos no tienen coordenadas GPS válidas para mostrar en el mapa'
              : 'No hay eventos para mostrar en el mapa'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mapa-eventos-container" style={{ height: altura }}>
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={true}
        doubleClickZoom={true}
        whenReady={() => {
          if (mapRef.current) {
            setTimeout(() => {
              mapRef.current.invalidateSize();
            }, 100);
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController eventos={eventosValidos} selectedEvent={selectedEvent} />
        
        {/* Componente para marcadores con clustering profesional */}
        <DynamicMarkers
          eventos={eventos}
          selectedEvent={selectedEvent}
          vehiculo={vehiculo}
          showPolyline={showPolyline}
          showNumbers={showNumbers}
        />
      </MapContainer>
    </div>
  );
}
